import {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type WheelEvent,
} from "react";
import styled from "@emotion/styled";
import {
  MemCommonEditorTheme,
  MemCommonMarkdown,
  MemCommonRichTextInputActionKind,
  MemCommonRichTextInputInstance,
  syntaxTreeModule,
} from "@mem-labs/common-editor";
import { useMutation, useQuery } from "convex/react";
import { observer } from "mobx-react-lite";
import { api, Id } from "@/smart-views/client/api";
import { setSmartViewTitle } from "@/smart-views/client/title-cache";
import { materializeSmartView } from "@/smart-views/client/backend-api";
import { useAppStore } from "@/store";
import { logger } from "@/modules/logger";
import { ThemeType, useTheme } from "@/mds/themes";
import { MDSButton, MDSButtonVariant } from "@/mds";
import {
  InstructionsEditor,
  InstructionsEditorFetchDataHandler,
  InstructionsEditorMentionItem,
} from "@/pages/integrations/instructions-editor";
import { collectChipIds, parseChipHref, parseChips } from "./chips";
import {
  collectMarkdownCitationNoteIds,
  renderMarkdownBlockPreviewBody,
} from "./markdown-preview-body";
import { CitationChip } from "../citations/CitationChip";

interface MarkdownBlockProps {
  blockId: Id<"blocks">;
  body: string;
  isReadOnly?: boolean;
  onUserAction?: (summary: string) => void;
}

export const MarkdownBlock: FC<MarkdownBlockProps> = observer(function MarkdownBlock({
  blockId,
  body,
  isReadOnly = false,
  onUserAction,
}: MarkdownBlockProps) {
  const updateContent = useMutation(api.smartViews.client.updateBlockContent);
  const { themeType } = useTheme();
  const { store } = useAppStore();

  // Resolve any chip tokens in the body so we can rewrite them as
  // mem-chip:// markdown links for MemCommonMarkdown. Tokens that don't
  // resolve fall back to a placeholder label rather than failing render.
  const referencedSmartViewIds = useMemo(() => {
    const segments = parseChips(body);
    return collectChipIds(segments).smartViewIds;
  }, [body]);
  const chipMetadata = useQuery(
    api.smartViews.client.getSmartViewChipMetadata,
    referencedSmartViewIds.length > 0 ? { smartViewIds: referencedSmartViewIds } : "skip"
  );
  // Follow redirects (the canonical row may have a different title).
  const redirectTargetIds = useMemo(() => {
    if (chipMetadata === undefined) return [];
    return chipMetadata
      .map(entry => (entry.exists && entry.redirectTo !== undefined ? entry.redirectTo : null))
      .filter((id): id is string => id !== null);
  }, [chipMetadata]);
  const redirectTargetMetadata = useQuery(
    api.smartViews.client.getSmartViewChipMetadata,
    redirectTargetIds.length > 0 ? { smartViewIds: redirectTargetIds } : "skip"
  );
  const chipResolverByOriginalId = useMemo(() => {
    const map = new Map<string, { title: string; targetId: string; isJit: boolean } | "missing">();
    if (chipMetadata === undefined) return map;
    const redirectMap = new Map<string, { title: string; status: string } | "missing">();
    if (redirectTargetMetadata !== undefined) {
      for (const entry of redirectTargetMetadata) {
        redirectMap.set(
          entry.smartViewId,
          entry.exists ? { title: entry.title, status: entry.status } : "missing"
        );
      }
    }
    for (const entry of chipMetadata) {
      if (!entry.exists) {
        map.set(entry.smartViewId, "missing");
        continue;
      }
      if (entry.redirectTo !== undefined) {
        const target = redirectMap.get(entry.redirectTo);
        if (target === undefined) continue; // still loading
        if (target === "missing") {
          map.set(entry.smartViewId, "missing");
          continue;
        }
        map.set(entry.smartViewId, {
          title: target.title,
          targetId: entry.redirectTo,
          isJit: target.status === "jit",
        });
        continue;
      }
      map.set(entry.smartViewId, {
        title: entry.title,
        targetId: entry.smartViewId,
        isJit: entry.status === "jit",
      });
    }
    return map;
  }, [chipMetadata, redirectTargetMetadata]);

  // Cache resolved titles globally so tab title resolution works after click.
  useEffect(() => {
    chipResolverByOriginalId.forEach(value => {
      if (value !== "missing") setSmartViewTitle(value.targetId, value.title);
    });
  }, [chipResolverByOriginalId]);

  const renderedBody = renderMarkdownBlockPreviewBody({
    body,
    resolveChipTitle: chip => {
      if (chip.kind === "note") {
        return getNoteChipTitle(store, chip.id);
      }

      const resolved = chipResolverByOriginalId.get(chip.id);
      if (resolved === undefined) return undefined;
      if (resolved === "missing") return "Unknown smart view";
      return resolved.title;
    },
  });
  const inputRef = useRef<MemCommonRichTextInputInstance>(null);
  const didHydrateEditorRef = useRef(false);
  const previewRef = useRef<HTMLDivElement | null>(null);
  const editorScrollRef = useRef<HTMLDivElement | null>(null);
  const [draftHtml, setDraftHtml] = useState<string | undefined>(undefined);
  const [isEditing, setIsEditing] = useState(false);
  const [renderedBodyHeight, setRenderedBodyHeight] = useState<number | null>(null);

  const bodyHtml = useMemo(() => markdownToEditorHtml(body), [body]);
  const citationNoteIds = useMemo(() => collectMarkdownCitationNoteIds(body), [body]);

  useEffect(() => {
    if (!isEditing || didHydrateEditorRef.current || draftHtml === undefined) {
      return;
    }

    const timeoutId = window.setTimeout(() => {
      didHydrateEditorRef.current = true;
      inputRef.current?.dispatchAction({
        kind: MemCommonRichTextInputActionKind.SetEditorContent,
        payload: { content: draftHtml },
      });
      inputRef.current?.dispatchAction({
        kind: MemCommonRichTextInputActionKind.FocusEditor,
        payload: { position: "end" },
      });
    }, 0);

    return () => window.clearTimeout(timeoutId);
  }, [draftHtml, isEditing]);

  const handlePreviewClick = useCallback(() => {
    if (isReadOnly) return;
    setRenderedBodyHeight(measureRenderedBodyHeight(previewRef.current));
    didHydrateEditorRef.current = false;
    setDraftHtml(bodyHtml);
    setIsEditing(true);
  }, [bodyHtml, isReadOnly]);

  const handleContentModified = useCallback(() => {
    inputRef.current?.dispatchAction({
      kind: MemCommonRichTextInputActionKind.SendContents,
      payload: null,
    });
  }, []);

  const handleFetchData = useCallback<
    InstructionsEditorFetchDataHandler<InstructionsEditorMentionItem>
  >(async () => ({ data: [], isLoading: false }), []);

  const handleEditorWheelCapture = useCallback((event: WheelEvent<HTMLDivElement>) => {
    const editor = editorScrollRef.current;

    if (!(editor instanceof HTMLElement) || event.deltaY === 0) {
      return;
    }

    const canScrollUp = editor.scrollTop > 0;
    const canScrollDown = editor.scrollTop + editor.clientHeight < editor.scrollHeight - 1;
    const shouldScrollEditor = event.deltaY < 0 ? canScrollUp : canScrollDown;

    if (shouldScrollEditor) {
      event.preventDefault();
      event.stopPropagation();
      editor.scrollTop += normalizeWheelDelta(event);
    }
  }, []);

  const handleSave = useCallback(() => {
    const nextBody = syntaxTreeModule.convertHtmlToMarkdown({
      htmlContent: draftHtml ?? "",
    });

    if (nextBody !== body) {
      void updateContent({ blockId, content: { kind: "markdown", body: nextBody } });
      onUserAction?.("Edited the section content.");
    }

    didHydrateEditorRef.current = false;
    setIsEditing(false);
  }, [blockId, body, draftHtml, onUserAction, updateContent]);

  const handleCancel = useCallback(() => {
    didHydrateEditorRef.current = false;
    setDraftHtml(bodyHtml);
    inputRef.current?.dispatchAction({
      kind: MemCommonRichTextInputActionKind.SetEditorContent,
      payload: { content: bodyHtml },
    });
    setIsEditing(false);
  }, [bodyHtml]);

  const handleClickMemItem = useCallback(() => {
    /** Smart View markdown does not route Mem mentions yet. */
  }, []);

  // Intercept clicks on chip links (mem-chip://...) before MemCommonMarkdown
  // tries to follow them. Returning `true` signals "handled".
  const handleClickLink = useCallback(
    (href: string, _target: HTMLElement, event: MouseEvent<Element>) => {
      const chip = parseChipHref(href);
      if (chip === null) return false;
      event.stopPropagation();

      if (chip.kind === "note") {
        store.navigation.goToNote({ noteId: chip.id });
        return true;
      }

      const resolved = chipResolverByOriginalId.get(chip.id);
      if (resolved === undefined || resolved === "missing") return true;

      const targetId = resolved.targetId;
      store.navigation.goToSmartView({ smartViewId: targetId });
      if (resolved.isJit) {
        materializeSmartView({ smartViewId: targetId }).catch(err => {
          logger.error({
            message: "Failed to materialize JIT smart view from markdown chip",
            info: { smartViewId: targetId, err: String(err) },
          });
        });
      }
      return true;
    },
    [chipResolverByOriginalId, store]
  );

  const editorTheme =
    themeType === ThemeType.Harvest
      ? MemCommonEditorTheme.Harvest
      : MemCommonEditorTheme.HarvestMoon;

  if (isReadOnly || !isEditing) {
    return (
      <MarkdownPreviewShell ref={previewRef} onClick={handlePreviewClick} $isEditable={!isReadOnly}>
        {renderedBody.trim() ? (
          <>
            <MarkdownPreview
              onClickMemItem={handleClickMemItem}
              onClickLink={handleClickLink}
              renderLinksAsChips
              contentAlignment="start"
              uiTheme={editorTheme}
            >
              {renderedBody}
            </MarkdownPreview>
            {citationNoteIds.length > 0 && (
              <InlineSources>
                {citationNoteIds.map(noteId => (
                  <CitationChip key={noteId} noteId={noteId} />
                ))}
              </InlineSources>
            )}
          </>
        ) : (
          <EmptyMarkdownPlaceholder>Start writing...</EmptyMarkdownPlaceholder>
        )}
      </MarkdownPreviewShell>
    );
  }

  return (
    <EditorShell $height={renderedBodyHeight}>
      <EditorScrollScope ref={editorScrollRef} onWheelCapture={handleEditorWheelCapture}>
        <StyledInstructionsEditor
          isDisabled={false}
          inputRef={inputRef}
          setContent={draftHtml}
          editorDimensions={{
            minHeight: 0,
            padding: { top: 12, right: 12, bottom: 12, left: 12 },
          }}
          placeholder="Start writing..."
          fetchData={handleFetchData}
          onContentModified={handleContentModified}
          onSubmit={({ htmlContent }) => setDraftHtml(htmlContent)}
          disableSetContent
          autoFocus
        />
      </EditorScrollScope>
      <EditorActions>
        <MDSButton onClick={handleCancel}>Cancel</MDSButton>
        <MDSButton variant={MDSButtonVariant.Contrast} onClick={handleSave}>
          Save
        </MDSButton>
      </EditorActions>
    </EditorShell>
  );
});

const markdownToEditorHtml = (markdown: string): string => {
  if (!markdown.trim()) {
    return "";
  }

  return syntaxTreeModule.convertMdxToHtml({ mdxContent: markdown });
};

const getNoteChipTitle = (
  store: ReturnType<typeof useAppStore>["store"],
  noteId: string
): string | undefined => {
  const note = store.notes.getNoteObservableById({ noteId });

  return note?.suggestionData?.suggested_primary_label || note?.title || undefined;
};

const measureRenderedBodyHeight = (element: HTMLElement | null): number | null => {
  const body = element?.closest("[data-smart-view-block-body]");
  const card = element?.closest("[data-smart-view-block-card]");

  if (!(body instanceof HTMLElement) || !(card instanceof HTMLElement)) {
    return null;
  }

  const cardRect = card.getBoundingClientRect();
  const bodyRect = body.getBoundingClientRect();
  const cardStyles = window.getComputedStyle(card);
  const cardPaddingBottom = Number.parseFloat(cardStyles.paddingBottom) || 0;

  return Math.ceil(cardRect.bottom - bodyRect.top - cardPaddingBottom);
};

const normalizeWheelDelta = (event: WheelEvent<HTMLDivElement>): number => {
  switch (event.deltaMode) {
    case 1:
      return event.deltaY * 16;
    case 2:
      return event.deltaY * event.currentTarget.clientHeight;
    default:
      return event.deltaY;
  }
};

const MarkdownPreviewShell = styled.div<{ $isEditable: boolean }>`
  width: 100%;
  cursor: ${({ $isEditable }) => ($isEditable ? "text" : "default")};
`;

const MarkdownPreview = styled(MemCommonMarkdown)`
  width: 100%;
  font-size: ${({ theme }) => theme.fontSizes["text-s"]};
  line-height: 1.65;
  color: ${({ theme }) => theme.colors.text.primary};
  user-select: text;
  text-align: start;
  word-spacing: normal;

  * {
    user-select: text;
  }

  && .markdown,
  && .markdown p,
  && .markdown li {
    text-align: start;
    word-spacing: normal;
  }

  a[href^="mem-chip://"]
  {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    max-width: min(220px, 100%);
    min-width: 0;
    padding: 1px 6px;
    border: 1px solid transparent;
    border-radius: 6px;
    background-color: ${({ theme }) => theme.colors.background.button.chip.default};
    color: ${({ theme }) => theme.colors.text.primary};
    font-size: ${({ theme }) => theme.fontSizes["text-xs"]};
    line-height: ${({ theme }) => theme.lineHeights["text-xs"]};
    text-decoration: none;
    vertical-align: baseline;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    user-select: none;
  }

  a[href^="mem-chip://"]:hover
  {
    background-color: ${({ theme }) => theme.colors.background.button.chip.hover};
    color: ${({ theme }) => theme.colors.text.primary};
    text-decoration: none;
  }

  a[href^="mem-chip://"]::before
  {
    content: "";
    width: 10px;
    height: 10px;
    border: 1.5px solid currentColor;
    border-radius: 2px;
    opacity: 0.65;
    flex: 0 0 auto;
  }

  a[href^="mem-chip://note/"]::before
  {
    height: 11px;
    border-radius: 1px;
  }

  && .markdown a.mem-chip {
    flex: 0 1 auto;
    max-width: 100%;
  }
`;

const EmptyMarkdownPlaceholder = styled.div`
  color: ${({ theme }) => theme.colors.text.tertiary};
  font-size: ${({ theme }) => theme.fontSizes["text-s"]};
  line-height: 1.65;
`;

const InlineSources = styled.div`
  display: flex;
  align-items: center;
  align-content: flex-start;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 4px;
  margin-top: ${({ theme }) => theme.spacing["2"]};
  max-width: 100%;
  min-width: 0;

  > * {
    flex: 0 1 auto;
  }
`;

const EditorShell = styled.div<{ $height: number | null }>`
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto;
  gap: ${({ theme }) => theme.spacing["3"]};
  height: ${({ $height }) => ($height ? `${$height}px` : "auto")};
  min-height: 0;
  overflow: hidden;
  position: relative;
`;

const EditorScrollScope = styled.div`
  display: block;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const StyledInstructionsEditor = styled(InstructionsEditor)`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  overflow: visible;

  [data-mds-name="instructions-editor-input-container"] {
    align-items: stretch;
    display: flex;
    min-height: 100%;
  }

  [data-mds-name="instructions-editor-input-container"] > div {
    align-items: stretch;
    display: flex;
    flex: 1;
    min-height: 0;
  }

  [data-mds-name="instructions-editor-input-container"] > div > div {
    display: flex;
    flex: 1;
    min-height: 0;
  }

  [data-mds-name="instructions-editor-input-container"] > div > div > div {
    display: flex;
    flex: 1;
    min-height: 0;
  }

  [data-mds-name="instructions-editor-input-container"] > div > div > div > div {
    display: flex;
    flex: 1;
    min-height: 0;
  }

  && .ProseMirror {
    min-height: 100%;
    max-height: none;
    overflow-y: visible;
  }
`;

const EditorActions = styled.div`
  display: flex;
  flex-shrink: 0;
  justify-content: flex-end;
  gap: ${({ theme }) => theme.spacing["2"]};
`;
